import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../lib/api';
import { useSeason } from '../contexts/SeasonContext';
import { SeasonSelector } from '../components/common/SeasonSelector';
import {
    Trophy, TrendingUp, Users, Zap, ChevronRight,
    Activity, Target, BarChart3, Clock
} from 'lucide-react';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell
} from 'recharts';

const StatCard = ({ icon: Icon, label, value, accent = "cyan" }) => {
    const colors = {
        cyan: 'bg-cyan-500/10 text-cyan-400',
        green: 'bg-green-500/10 text-green-400',
        purple: 'bg-purple-500/10 text-purple-400',
        yellow: 'bg-yellow-500/10 text-yellow-400',
    };
    return (
        <div className="glass-card p-5 flex items-center gap-4 animate-fadeInUp" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colors[accent]}`}>
                <Icon className="w-6 h-6" />
            </div>
            <div>
                <div className="tech-mono text-[10px] text-white/40">{label}</div>
                <div className="heading-primary text-2xl text-white">{value}</div>
            </div>
        </div>
    );
};

export default function DashboardPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const { currentSeason } = useSeason();

    useEffect(() => {
        if (!currentSeason) return;
        setLoading(true);
        API.get(`/dashboard?season=${encodeURIComponent(currentSeason)}`).then(res => {
            setData(res.data);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, [currentSeason]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }
    if (!data) return null;

    const teamStrengthData = data.top_teams?.map(t => ({
        name: t.short_name, strength: t.strength, fill: t.color,
    })) || [];

    return (
        <div className="space-y-6" data-testid="dashboard-page">
            {/* Season Selector + Stats */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <SeasonSelector />
                {data.stats?.ai_trained && (
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-green-500/10 border border-green-500/20">
                        <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                        <span className="text-[10px] text-green-400 tech-mono">AI TRAINED — {data.stats.total_all_matches || 0} MATCHES</span>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard icon={Trophy} label="SEASON MATCHES" value={data.stats?.total_matches || 0} accent="cyan" />
                <StatCard icon={Users} label="PLAYERS TRACKED" value={data.stats?.total_players || 0} accent="green" />
                <StatCard icon={Target} label={`AI ACCURACY (${data.stats?.predictions_analyzed || 0} analyzed)`} value={`${data.stats?.ai_accuracy || 0}%`} accent="purple" />
                <StatCard icon={Zap} label={`2026 COMPLETED`} value={data.stats?.completed_matches_2026 || 0} accent="yellow" />
            </div>

            {/* Season counts */}
            {data.stats?.season_counts && (
                <div className="flex gap-2 flex-wrap" data-testid="season-badges">
                    {Object.entries(data.stats.season_counts).sort(([a],[b]) => b.localeCompare(a)).slice(0, 6).map(([season, count]) => (
                        <div key={season} className={`glass-card px-3 py-1.5 flex items-center gap-2 ${season === data.season ? 'border border-cyan-500/30' : ''}`}>
                            <div className={`w-1.5 h-1.5 rounded-full ${season === data.season ? 'bg-cyan-400' : 'bg-white/20'}`} />
                            <span className="tech-mono text-[10px] text-white/50">{season}</span>
                            <span className="text-[10px] text-white/70 font-bold">{count}</span>
                        </div>
                    ))}
                    <div className="glass-card px-3 py-1.5 flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                        <span className="tech-mono text-[10px] text-white/50">Source</span>
                        <span className="text-[10px] text-cyan-400 font-bold uppercase">{data.stats?.data_source || 'demo'}</span>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                <div className="lg:col-span-8 space-y-4">
                    {data.live_matches?.length > 0 && (
                        <div className="glass-card overflow-hidden">
                            <div className="flex items-center gap-2 p-4 border-b border-white/5 bg-white/5">
                                <div className="badge-live pulse-live">LIVE</div>
                                <span className="heading-primary text-sm text-white">LIVE MATCHES</span>
                            </div>
                            {data.live_matches.map(match => (
                                <div key={match.id} data-testid={`live-match-${match.id}`} className="p-4 flex items-center justify-between hover:bg-white/5 transition-colors cursor-pointer"
                                    onClick={() => navigate(`/match-lab?match=${match.id}`)}>
                                    <div className="flex items-center gap-6">
                                        <div className="text-right w-28">
                                            <div className="heading-primary text-sm text-white">{match.home_team?.short_name}</div>
                                            <div className="text-xs text-white/40">{match.home_score || '-'}</div>
                                        </div>
                                        <div className="text-xs text-white/30 tech-mono">VS</div>
                                        <div className="w-28">
                                            <div className="heading-primary text-sm text-white">{match.away_team?.short_name}</div>
                                            <div className="text-xs text-white/40">{match.away_score || '-'}</div>
                                        </div>
                                    </div>
                                    <ChevronRight className="w-4 h-4 text-white/20" />
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="glass-card overflow-hidden">
                        <div className="flex items-center justify-between p-4 border-b border-white/5 bg-white/5">
                            <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-cyan-400" />
                                <span className="heading-primary text-sm text-white">UPCOMING PREDICTIONS</span>
                            </div>
                            <div className="badge-ai">AI POWERED</div>
                        </div>
                        <div className="divide-y divide-white/5">
                            {data.quick_predictions?.map((pred, i) => (
                                <div key={pred.match_id}
                                    data-testid={`prediction-card-${pred.match_id}`}
                                    className={`p-4 hover:bg-white/5 transition-colors cursor-pointer animate-fadeInUp stagger-${i + 1}`}
                                    onClick={() => navigate(`/match-lab?match=${pred.match_id}`)}>
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-4">
                                            <span className="heading-primary text-sm text-white">{pred.home_team?.short_name}</span>
                                            <span className="text-xs text-white/30 tech-mono">VS</span>
                                            <span className="heading-primary text-sm text-white">{pred.away_team?.short_name}</span>
                                        </div>
                                        <div className="text-[10px] text-white/30 tech-mono">
                                            {new Date(pred.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                        </div>
                                    </div>
                                    <div className="prob-bar">
                                        <div className="prob-bar-fill bg-cyan-500" style={{ width: `${pred.home_win_pct}%` }} />
                                    </div>
                                    <div className="flex justify-between mt-1">
                                        <span className="text-xs neon-text font-bold">{pred.home_win_pct}%</span>
                                        <span className="text-xs text-white/40">{pred.venue?.city}</span>
                                        <span className="text-xs neon-text-pink font-bold">{pred.away_win_pct}%</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="glass-card overflow-hidden">
                        <div className="flex items-center gap-2 p-4 border-b border-white/5 bg-white/5">
                            <Activity className="w-4 h-4 text-green-400" />
                            <span className="heading-primary text-sm text-white">RECENT RESULTS</span>
                        </div>
                        <div className="divide-y divide-white/5">
                            {data.recent_results?.map(match => (
                                <div key={match.id} className="p-4 flex items-center justify-between cursor-pointer hover:bg-white/5 transition-colors"
                                    onClick={() => navigate(`/vimal?match=${match.id}`)}
                                    data-testid={`recent-result-${match.id}`}>
                                    <div className="flex items-center gap-4">
                                        <div className={`text-right w-24 ${match.winner === match.home_team?.id ? 'text-green-400' : 'text-white/60'}`}>
                                            <div className="heading-primary text-sm">{match.home_team?.short_name}</div>
                                            <div className="text-xs">{match.home_score}</div>
                                        </div>
                                        <div className="text-xs text-white/30 tech-mono">VS</div>
                                        <div className={`w-24 ${match.winner === match.away_team?.id ? 'text-green-400' : 'text-white/60'}`}>
                                            <div className="heading-primary text-sm">{match.away_team?.short_name}</div>
                                            <div className="text-xs">{match.away_score}</div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="text-[10px] text-white/30 tech-mono">{match.venue?.city}</div>
                                        <ChevronRight className="w-3.5 h-3.5 text-white/15" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-4 space-y-4">
                    <div className="glass-card overflow-hidden">
                        <div className="flex items-center gap-2 p-4 border-b border-white/5 bg-white/5">
                            <BarChart3 className="w-4 h-4 text-yellow-400" />
                            <span className="heading-primary text-sm text-white">TEAM STRENGTH</span>
                        </div>
                        <div className="p-4">
                            <ResponsiveContainer width="100%" height={200}>
                                <BarChart data={teamStrengthData} layout="vertical">
                                    <XAxis type="number" domain={[0, 100]} hide />
                                    <YAxis type="category" dataKey="name" width={40} tick={{ fill: '#999', fontSize: 11, fontFamily: 'Barlow Condensed' }} />
                                    <Tooltip contentStyle={{ background: '#000', border: '1px solid #333', borderRadius: 8 }} labelStyle={{ color: '#fff' }} />
                                    <Bar dataKey="strength" radius={[0, 4, 4, 0]}>
                                        {teamStrengthData.map((entry, index) => (
                                            <Cell key={index} fill={entry.fill} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <div className="glass-card overflow-hidden">
                        <div className="flex items-center gap-2 p-4 border-b border-white/5 bg-white/5">
                            <TrendingUp className="w-4 h-4 text-purple-400" />
                            <span className="heading-primary text-sm text-white">PLAYER IMPACT</span>
                        </div>
                        <div className="divide-y divide-white/5">
                            {data.top_players?.slice(0, 8).map((player, i) => (
                                <div key={player.id} className="flex items-center justify-between p-3 px-4">
                                    <div className="flex items-center gap-3">
                                        <span className="text-xs text-white/30 w-4 tech-mono">{i + 1}</span>
                                        <div>
                                            <div className="text-sm text-white">{player.name}</div>
                                            <div className="text-[10px] text-white/40 tech-mono">{player.role}</div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="text-sm neon-text font-bold">{player.impact_score}</div>
                                        <div className={`w-2 h-2 rounded-full ${player.form > 0.85 ? 'bg-green-400' : player.form > 0.7 ? 'bg-yellow-400' : 'bg-red-400'}`} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
